"use client";

import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useCallback } from "react";
import newrelic from "@/lib/monitoring/newrelic";

/**
 * Custom hook for reCAPTCHA v3 integration
 *
 * Provides a simplified interface for executing reCAPTCHA challenges
 * with built-in error handling and monitoring.
 */
export function useRecaptcha() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  /**
   * Execute reCAPTCHA for a specific action
   *
   * @param action - The action name for reCAPTCHA (e.g., 'register', 'login')
   * @returns Promise<string | null> - The reCAPTCHA token or null if failed
   */
  const executeRecaptchaAction = useCallback(
    async (action: string): Promise<string | null> => {
      try {
        if (!executeRecaptcha) {
          console.warn("reCAPTCHA not available");
          return null;
        }

        const token = await executeRecaptcha(action);

        if (!token) {
          throw new Error("Failed to generate reCAPTCHA token");
        }

        return token;
      } catch (error) {
        console.error("reCAPTCHA execution failed:", error);

        newrelic.noticeError(error as Error, {
          action,
          context: "executeRecaptcha",
        });

        return null;
      }
    },
    [executeRecaptcha],
  );

  return {
    executeRecaptcha: executeRecaptchaAction,
    isRecaptchaAvailable: !!executeRecaptcha,
  };
}
